import { normalizeLayout, resolveReceiptLocale, sampleReceipt, type ReceiptData, type ReceiptLayout } from "@astroplanet/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { keys } from "../../hooks/queries";
import { useReceiptLayout } from "../../hooks/hardware";
import { receiptIdentity } from "../../hooks/usePrintReceipt";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { useHardwareSnapshot } from "../../lib/hardwareStore";
import type { Company } from "../../lib/types";
import { Pill, SectionHeader } from "../ui/Badges";
import { ReceiptPreview } from "./ReceiptPreview";

type Scope = "branch" | "terminal";

function Toggle({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2 text-xs font-bold">
      <span>{label}{hint && <span className="block text-[10px] font-medium text-gray-500">{hint}</span>}</span>
      <input type="checkbox" role="switch" aria-label={label} className="h-4 w-4 flex-shrink-0" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

/**
 * Receipt builder: language (Arabic right-to-left / English left-to-right), logo, optional lines, footer. Saved for the whole branch so every
 * terminal prints the same receipt, with an optional override for one terminal. The preview is drawn by the same function the printer uses.
 */
export function ReceiptSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const hw = useHardwareSnapshot();
  const view = useReceiptLayout();
  const company = useQuery({ queryKey: keys.company, queryFn: () => api.get<{ company: Company }>("/settings/company") });
  const [scope, setScope] = useState<Scope>("branch");
  const [draft, setDraft] = useState<ReceiptLayout | null>(null);
  const [paper, setPaper] = useState<58 | 80>(80);
  const [sampleKind, setSampleKind] = useState<"full" | "minimal">("full");
  const [busy, setBusy] = useState(false);

  // load the layer being edited whenever it (or the scope) changes
  useEffect(() => {
    if (!view.data) return;
    setDraft(scope === "branch" ? view.data.branch : normalizeLayout(view.data.terminal ?? {}, view.data.layout));
  }, [view.data, scope]);
  useEffect(() => { if (hw.paperWidth === 58 || hw.paperWidth === 80) setPaper(hw.paperWidth); }, [hw.paperWidth]);

  const set = <K extends keyof ReceiptLayout>(k: K, v: ReceiptLayout[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  const receiptLocale = draft ? resolveReceiptLocale(draft.language, locale) : locale;
  const data: ReceiptData | null = useMemo(() => {
    if (!draft) return null;
    const base = sampleReceipt(receiptLocale);
    const ident = company.data ? receiptIdentity(company.data.company, receiptLocale, { phone: t("company.phone"), taxNo: t("company.taxNo") }) : null;
    const withCompany: ReceiptData = { ...base, ...(ident ? { stationName: ident.stationName, headerLines: ident.headerLines.length ? ident.headerLines : base.headerLines, footer: ident.footer } : {}), layout: draft };
    return sampleKind === "full" ? withCompany : { ...withCompany, discount: 0, discountLabel: null, tax: 0, subtotal: 6000, total: 6000, items: withCompany.items.slice(0, 1).map((i) => ({ ...i, qty: 1, lineTotal: 5000 })), payments: [{ method: "card", amount: 6000 }] };
  }, [draft, receiptLocale, company.data, sampleKind, t]);

  async function save() {
    if (!draft || !view.data) return;
    setBusy(true);
    try {
      if (scope === "branch") await api.put("/receipt-layout", draft);
      else {
        // only what differs from the branch layout is an override; keys equal to the branch value are released (null)
        const patch: Record<string, unknown> = {};
        for (const k of Object.keys(draft) as Array<keyof ReceiptLayout>) patch[k] = draft[k] !== view.data.branch[k] ? draft[k] : null;
        await api.put("/receipt-layout/terminal", patch);
      }
      await qc.invalidateQueries({ queryKey: ["hw", "receipt-layout"] });
      toast(t("hw.saved"), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  async function resetTerminal() {
    setBusy(true);
    try {
      await api.delete("/receipt-layout/terminal");
      await qc.invalidateQueries({ queryKey: ["hw", "receipt-layout"] });
      toast(t("hw.overrideCleared"), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  async function printThis() {
    if (!data || !window.hardware) return;
    setBusy(true);
    try {
      await window.hardware.printReceipt({ ...data, receiptNo: "PREVIEW", barcode: draft?.showBarcode ? data.barcode : undefined } as never);
      toast(t("hw.testSent"), "success");
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!view.data || !draft || !data) return <div className="card p-5 text-xs font-bold text-gray-500">{t("common.loading")}</div>;
  const dis = !canEdit;
  const inheritedFromBranch = scope === "terminal" && view.data.terminalOverrideKeys.length === 0;

  return (
    <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_auto]" aria-label={t("hw.receipt.title")}>
      <div className="card space-y-4 p-5">
        <SectionHeader icon="🧾" title={t("hw.receipt.title")} subtitle={t("hw.receipt.subtitle")} />

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t("hw.receipt.scope")}>
          {(["branch", "terminal"] as const).map((s) => (
            <button key={s} type="button" aria-pressed={scope === s} onClick={() => setScope(s)} className={`rounded-xl px-3 py-1.5 text-xs font-black ${scope === s ? "bg-cosmic-purple text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              {s === "branch" ? t("hw.receipt.scopeBranch") : t("hw.receipt.scopeTerminal", { id: view.data.terminalId })}
            </button>
          ))}
          {scope === "terminal" && <Pill tone={inheritedFromBranch ? "gray" : "purple"}>{inheritedFromBranch ? t("hw.receipt.inherits") : t("hw.receipt.overrides", { count: view.data.terminalOverrideKeys.length })}</Pill>}
        </div>
        {!canEdit && <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">{t("hw.receipt.managerOnly")}</p>}

        <fieldset disabled={dis} className="space-y-4 disabled:opacity-70">
          <div>
            <span className="label">{t("hw.receipt.language")}</span>
            <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={t("hw.receipt.language")}>
              {(["ui", "ar", "en"] as const).map((l) => (
                <label key={l} className={`cursor-pointer rounded-2xl border-2 p-3 text-xs font-bold transition ${draft.language === l ? "border-cosmic-purple bg-purple-50" : "border-gray-200 bg-gray-50 hover:border-gray-300"}`}>
                  <input type="radio" name="receipt-language" className="sr-only" checked={draft.language === l} onChange={() => set("language", l)} />
                  <div className="text-sm font-black">{t(`hw.receipt.lang.${l}`)}</div>
                  <div className="mt-0.5 text-[10px] font-medium text-gray-500">{t(`hw.receipt.lang.${l}Hint`)}</div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <Toggle label={t("hw.receipt.showLogo")} checked={draft.showLogo} onChange={(v) => set("showLogo", v)} />
            <Toggle label={t("hw.receipt.showHeaderLines")} hint={t("hw.receipt.showHeaderLinesHint")} checked={draft.showHeaderLines} onChange={(v) => set("showHeaderLines", v)} />
            <Toggle label={t("hw.receipt.showTaxLine")} checked={draft.showTaxLine} onChange={(v) => set("showTaxLine", v)} />
            <Toggle label={t("hw.receipt.showPayments")} checked={draft.showPayments} onChange={(v) => set("showPayments", v)} />
            <Toggle label={t("hw.receipt.showTerminal")} checked={draft.showTerminal} onChange={(v) => set("showTerminal", v)} />
            <Toggle label={t("hw.receipt.showUnitPrice")} checked={draft.showUnitPrice} onChange={(v) => set("showUnitPrice", v)} />
            <Toggle label={t("hw.receipt.showBarcode")} hint={t("hw.receipt.showBarcodeHint")} checked={draft.showBarcode} onChange={(v) => set("showBarcode", v)} />
            <Toggle label={t("hw.receipt.showQr")} checked={draft.showQr} onChange={(v) => set("showQr", v)} />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block"><span className="label">{t("hw.receipt.fontSize")}</span>
              <select className="field" value={draft.fontSize} onChange={(e) => set("fontSize", e.target.value as ReceiptLayout["fontSize"])}>{(["small", "normal", "large"] as const).map((f) => <option key={f} value={f}>{t(`hw.receipt.font.${f}`)}</option>)}</select></label>
            <label className="block"><span className="label">{t("hw.receipt.itemNames")}</span>
              <select className="field" value={draft.itemNames} onChange={(e) => set("itemNames", e.target.value as ReceiptLayout["itemNames"])}>{(["language", "both"] as const).map((f) => <option key={f} value={f}>{t(`hw.receipt.names.${f}`)}</option>)}</select></label>
            <label className="block"><span className="label">{t("hw.receipt.logoWidth", { percent: draft.logoWidthPercent })}</span>
              <input type="range" min={30} max={90} step={2} className="w-full" value={draft.logoWidthPercent} disabled={!draft.showLogo} onChange={(e) => set("logoWidthPercent", Number(e.target.value))} aria-label={t("hw.receipt.logoWidth", { percent: draft.logoWidthPercent })} /></label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="label">{t("hw.receipt.extraHeader")}</span><input className="field" maxLength={120} value={draft.extraHeader} onChange={(e) => set("extraHeader", e.target.value)} /></label>
            <label className="block"><span className="label">{t("hw.receipt.qrText")}</span><input className="field num" maxLength={200} disabled={!draft.showQr} value={draft.qrText} onChange={(e) => set("qrText", e.target.value)} placeholder="https://…" /></label>
            <label className="block"><span className="label">{t("hw.receipt.footerAr")}</span><input className="field" dir="rtl" maxLength={160} value={draft.footerAr} onChange={(e) => set("footerAr", e.target.value)} /></label>
            <label className="block"><span className="label">{t("hw.receipt.footerEn")}</span><input className="field" dir="ltr" maxLength={160} value={draft.footerEn} onChange={(e) => set("footerEn", e.target.value)} /></label>
          </div>
          <p className="text-[10px] font-semibold text-gray-500">{t("hw.receipt.footerHelp")}</p>
        </fieldset>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap gap-2">
            {scope === "terminal" && view.data.terminalOverrideKeys.length > 0 && canEdit && <button type="button" className="btn-ghost" disabled={busy} onClick={() => void resetTerminal()}>{t("hw.receipt.resetOverride")}</button>}
            {hw.native && <button type="button" className="btn-dark" disabled={busy} onClick={() => void printThis()}>🖨️ {t("hw.receipt.printThis")}</button>}
          </div>
          {canEdit && <button type="button" className="btn-primary" disabled={busy} onClick={() => void save()}>{scope === "branch" ? t("hw.receipt.saveBranch") : t("hw.receipt.saveTerminal")}</button>}
        </div>
      </div>

      <div className="min-w-0 space-y-3">
        <div className="card min-w-0 space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-black uppercase tracking-wide text-gray-500">{t("hw.preview.title")}</h3>
            <div className="flex gap-1.5">
              {([80, 58] as const).map((w) => <button key={w} type="button" aria-pressed={paper === w} onClick={() => setPaper(w)} className={`rounded-lg px-2.5 py-1 text-[11px] font-black ${paper === w ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-600"}`}>{w} mm</button>)}
              <button type="button" aria-pressed={sampleKind === "minimal"} onClick={() => setSampleKind(sampleKind === "full" ? "minimal" : "full")} className="rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-black text-gray-600">{sampleKind === "full" ? t("hw.preview.simple") : t("hw.preview.full")}</button>
            </div>
          </div>
          <ReceiptPreview data={data} paperWidth={paper} />
          <p className="text-center text-[10px] font-semibold text-gray-500">{t("hw.preview.help", { lang: t(`hw.receipt.lang.${receiptLocale}Short`) })}</p>
        </div>
      </div>
    </section>
  );
}

